import { defineComponent as _defineComponent } from 'vue'
import type { Category } from '@ilteducation/content-ref-data-types';
import { computed, ref, watch, getCurrentInstance } from 'vue';
import DisableableButton from '@/components/buttons/DisableableButton.vue';
import { defaultSubscriptions } from '@/foundations/helpers/defaultSubscriptions';
import { defaultRealms } from '@/foundations/helpers/defaultRealms';
import useMediaFormFeature from '@/comp/use-media-form-feature';
import { getAllRefData } from '@/data-provider/refDataProvider';
import AreaOrderEdit from '@/components/areas/AreaOrderEdit.vue';
import eventBus from '@/foundations/services/eventBus';
import IconPreview from '@/components/IconPreview.vue';
import { CATEGORY_SELECT_ICONS, categorySelectIconClass } from '@/resources/categories/category-select-icons';

type Props = {
  id?: string;
  title: string;
  item?: Category;
  isCreate?: boolean;
};

export default /*#__PURE__*/_defineComponent({
  __name: 'Form',
  props: {
    id: null,
    title: null,
    item: null,
    isCreate: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props as { id?: string, title: string, item?: Category, isCreate: boolean }


const availableMarkets = ref(defaultRealms);
const availableSubscriptions = ref(defaultSubscriptions);
const notEmptyRule = ref([(val: any) => (val || '').length > 0 || 'This field is required']);
const DefaultItem = ref<Partial<Category>>({
  active: true,
  usedInReadingProfile: false,
});

// @TODO: should be color picker instead
const availableColors = [
  { value: 'green', text: 'Green (#62b878)' },
  { value: 'pink', text: 'Pink (#be77a6)' },
  { value: 'teal', text: 'Teal (#5dc7bb)' },
  { value: 'blue', text: 'Blue (#56a4d7)' },
  { value: 'purple', text: 'Purple (#8169af)' },
  { value: 'blush', text: 'Blush (#d37777)' },
  { value: 'yellow', text: 'Yellow (#cf9d58)' },
  { value: 'gray', text: 'Gray (#596a79)' },
  { value: 'lightpink', text: 'Light Pink (#e090a7)' },
  { value: 'darkblue', text: 'Dark Blue (#124c8F)' },
  { value: 'orange', text: 'Orange (#E85656)' },
  { value: 'highlighterred', text: 'Highlighter Red (#E85656)' },
  { value: 'gold', text: 'Gold (#F3BF39)' },
  { value: 'sybaritegreen', text: 'Sybarite Green (#89CAAD)' },
  { value: 'black', text: 'Black (#0F0E11)' },
  { value: 'hkk', text: 'Hkk (#9ACD48)' },
  { value: 'ft', text: 'Ft (#3C92AA)' },
  { value: 'sl', text: 'Sl (#C6684D)' },
];

const availableRoles = ['employee', 'student', 'guardian'];

const availableCategoryKinds = [
  { value: 'subject', text: 'Subject' },
  { value: 'grade', text: 'Grade' },
  { value: 'shortcut', text: 'Shortcut' },
];

const allCategories = ref([] as Category[]);
const shownCategories = computed(() => {
  const itemRealms = props.item?.realms || [];
  const itemSubscriptionTypes = props.item?.subscriptionTypes || [];

  if (!itemRealms.length || !itemSubscriptionTypes.length) {
    return [];
  }

  return allCategories.value
    .filter((possibleParentCategory) =>
      itemRealms.every((editedCategoryRealm) => possibleParentCategory.realms?.includes(editedCategoryRealm)),
    )
    .filter((possibleParentCategory) =>
      itemSubscriptionTypes.every((editedCategorySubscription) =>
        possibleParentCategory.subscriptionTypes?.includes(editedCategorySubscription),
      ),
    )
    .filter((possibleParentCategory) => possibleParentCategory.id !== props.item?.id);
});
const selectedSubscriptionTypes = ref<string[]>(props.item?.subscriptionTypes ?? []);
const isManabuaCategory = computed(() =>
  (selectedSubscriptionTypes.value ?? []).some((sub) => sub.includes('immigrant')),
);

// Tracked separately from the form model so the icon picker appears as soon as the 'shortcut' kind is set on a new category.
const selectedKind = ref<string>(props.item?.kind ?? '');
const selectedIcon = ref<string | null>(props.item?.icon ?? null);

watch(selectedIcon, (icon) => {
  DefaultItem.value = { ...DefaultItem.value, icon: icon ?? '' };
});

const isPolylinoSubscription = computed(() =>
  (selectedSubscriptionTypes.value ?? []).some((sub) => sub.includes('poly')),
);

const isPolylinoMaterialCategory = computed(() => isPolylinoSubscription.value && selectedKind.value === 'shortcut');

const iconStyles = [
  { text: 'Duotone', value: 'fad' },
  { text: 'Duotone Light', value: 'fadl' },
  { text: 'Solid', value: 'fas' },
];
const iconStyle = ref('fad');
const iconName = ref('');
const iconValue = computed(() => (iconName.value ? `${iconStyle.value} fa-${iconName.value}` : ''));

watch(
  () => props.item?.icon,
  (icon) => {
    const [style, name] = (icon || '').split(' fa-');
    if (style && name) {
      iconStyle.value = style;
      iconName.value = name;
    }
  },
  { immediate: true },
);

// vuetify-admin only writes a field to the save payload on user input, so a derived value never reaches
// it — push the combined icon into the form model (its v-model) directly.
watch(
  iconValue,
  (icon) => {
    if (isManabuaCategory.value) {
      DefaultItem.value = { ...DefaultItem.value, icon };
    }
  },
  { immediate: true },
);

getAllRefData('categories').then((categories) => {
  allCategories.value = categories.sort((a: Category, b: Category) => a.category.localeCompare(b.category));
});

const requiredMultiple = ref((value: unknown[]) => value.length > 0 || 'This field is required');
const disallowRemovingMarkets = ref(
  (markets: unknown[]) => props.item?.realms?.every((r) => markets.includes(r)) || "Can't remove markets",
);

const bookField = ref([
  {
    source: 'isbn',
    sortable: true,
  },
  {
    source: 'title',
    sortable: true,
  },
  { source: 'id', link: 'edit', sortable: false },
]);

const association = ref({
  resource: 'categories',
  source: '/categories/-_id_association',
  id: props.id,
});

const listInclude = ref([
  {
    contentType: 'Book',
  },
]);
const store = ref<any>();
const useStore = () => {
  const vm = getCurrentInstance()!.proxy;
  store.value = vm.$store;
};
useStore();
const { saveButtonAvailable, isCloneMode } = useMediaFormFeature(props.item);

const dispatch = async (op: 'add' | 'remove', categoryId: string, bookId: string) => {
  await store.value.dispatch(`books/update`, {
    id: bookId,
    data: {
      [`${op}_/categories/-_id_association`]: categoryId,
    },
  });
};

const selectedCategoryIDToAssociateTo = ref('');
const bulkAssociate = async (items: any[]) => {
  await Promise.all(
    items.map(async (item: any) => {
      if (!item.categories.includes(selectedCategoryIDToAssociateTo.value)) {
        await dispatch('add', selectedCategoryIDToAssociateTo.value, item.id);
      }
    }),
  );
  eventBus.$emit('snackbar.open', `Association to new category is finished for ${items.length} items`);
};

const bulkDissociate = async (items: any[]) => {
  await Promise.all(
    items.map(async (item: any) => {
      if (props.item?.id) await dispatch('remove', props.item?.id, item.id);
    }),
  );
  eventBus.$emit('snackbar.open', `Dissociation from the current category is finished for ${items.length} items`);
};

return { __sfc: true,props, availableMarkets, availableSubscriptions, notEmptyRule, DefaultItem, availableColors, availableRoles, availableCategoryKinds, allCategories, shownCategories, selectedSubscriptionTypes, isManabuaCategory, selectedKind, selectedIcon, isPolylinoSubscription, isPolylinoMaterialCategory, iconStyles, iconStyle, iconName, iconValue, requiredMultiple, disallowRemovingMarkets, bookField, association, listInclude, store, useStore, saveButtonAvailable, isCloneMode, dispatch, selectedCategoryIDToAssociateTo, bulkAssociate, bulkDissociate, DisableableButton, AreaOrderEdit, IconPreview, CATEGORY_SELECT_ICONS, categorySelectIconClass }
}

})